import React, { Component, createRef } from "react";
import { Link } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import Tagify from "@yaireo/tagify";
import "@yaireo/tagify/dist/tagify.css";
import { withRouter } from "../../utils/withRouter";

class ProductForm extends Component {
  constructor(props) {
    super(props);

    const { id } = props.router.params || {};
    this.isEditMode = Boolean(id);

    this.state = {
      id: id || null,
      form: {
        product_name: "",
        category_id: "",
        sub_category_id: "",
        child_category_id: "",
        brand_id: "",
        tag_ids: "",
        description: "",
        regular_price: "",
        sales_price: "",
        cost_price: "",
        stock_value: "",
        sku: "",
        product_specification: "",
        others_specification: "",
        model: "",
        seo_title: "",
        seo_description: "",
        seo_keywords: "",
        product_img: null,
      },
      options: {
        categories: [],
        subCategories: [],
        childCategories: [],
        brands: [],
        tags: [],
      },
    };

    this.tagInputRef = createRef();
    this.tagify = null;
  }

  async componentDidMount() {
    await this.fetchOptions();
    if (this.isEditMode) {
      await this.fetchProduct(this.state.id);
      // Load dependent dropdowns for edit mode
      if (this.state.form.category_id) {
        await this.fetchSubCategories(this.state.form.category_id, false);
      }
      if (this.state.form.sub_category_id) {
        await this.fetchChildCategories(this.state.form.sub_category_id, false);
      }
    }
    this.initTagify();
  }

  componentWillUnmount() {
    if (this.tagify) this.tagify.destroy();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.options.tags !== this.state.options.tags) {
      this.initTagify();
    }
  }

  /** Initialize Tagify **/
  initTagify = () => {
    if (!this.tagInputRef.current) return;

    if (this.tagify) this.tagify.destroy();

    this.tagify = new Tagify(this.tagInputRef.current, {
      whitelist: this.state.options.tags.map((t) => t.tag_name),
      dropdown: { enabled: 1, maxItems: 10 },
    });

    this.tagify.on("change", (e) => {
      const tags = e.detail.tagify.value.map((t) => t.value);
      this.setState((prev) => ({
        form: { ...prev.form, tag_ids: tags.join(",") },
      }));
    });

    // populate tags if editing
    if (this.isEditMode && this.state.form.tag_ids) {
      this.tagify.addTags(this.state.form.tag_ids.split(","));
    }
  };

  /** Fetch base dropdown data **/
  fetchOptions = async () => {
    try {
      const [catRes, brandRes, tagRes] = await Promise.all([
        axios.get("/products/categories"),
        axios.get("/products/brands"),
        axios.get("/products/tags"),
      ]);

      this.setState({
        options: {
          categories: catRes.data?.data || [],
          subCategories: [],
          childCategories: [],
          brands: brandRes.data?.data || [],
          tags: tagRes.data?.data || [],
        },
      });
    } catch (err) {
      console.error("Error fetching options:", err);
      Swal.fire("Error", "Failed to load product options", "error");
    }
  };

  /** Fetch sub categories by category id **/
  fetchSubCategories = async (categoryId, reset = true) => {
    if (!categoryId) {
      this.setState((prev) => ({
        options: { ...prev.options, subCategories: [], childCategories: [] },
        form: { ...prev.form, sub_category_id: "", child_category_id: "" },
      }));
      return;
    }

    try {
      const res = await axios.get(`/products/sub_categories?category_id=${categoryId}`);
      this.setState((prev) => ({
        options: { ...prev.options, subCategories: res.data?.data || [], childCategories: [] },
        form: reset
          ? { ...prev.form, sub_category_id: "", child_category_id: "" }
          : prev.form,
      }));
    } catch (err) {
      console.error("Error fetching sub categories:", err);
    }
  };

  /** Fetch child categories by sub category id **/
  fetchChildCategories = async (subCategoryId, reset = true) => {
    if (!subCategoryId) {
      this.setState((prev) => ({
        options: { ...prev.options, childCategories: [] },
        form: { ...prev.form, child_category_id: "" },
      }));
      return;
    }

    try {
      const res = await axios.get(`/products/child_categories?sub_category_id=${subCategoryId}`);
      this.setState((prev) => ({
        options: { ...prev.options, childCategories: res.data?.data || [] },
        form: reset ? { ...prev.form, child_category_id: "" } : prev.form,
      }));
    } catch (err) {
      console.error("Error fetching child categories:", err);
    }
  };

  /** Fetch product by id (for edit mode) **/
  fetchProduct = async (id) => {
    try {
      const res = await axios.get(`/products/${id}`);
      if (res.data.status === "success") {
        const data = res.data.data;
        this.setState({
          form: {
            ...this.state.form,
            ...data,
            product_img: null,
            tag_ids: data.tag_ids || "",
          },
        });
      } else {
        Swal.fire("Error", "Product not found", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch product data", "error");
    }
  };

  /** Handle all input changes **/
  handleChange = (e) => {
    const { name, value, files } = e.target;

    this.setState(
      (prev) => ({
        form: { ...prev.form, [name]: files ? files[0] : value },
      }),
      async () => {
        if (name === "category_id") {
          await this.fetchSubCategories(value);
        }
        if (name === "sub_category_id") {
          await this.fetchChildCategories(value);
        }
      }
    );
  };

  /** Handle form submit **/
  handleSubmit = async (e) => {
    e.preventDefault();
    const { form, id } = this.state;

    const formData = new FormData();
    Object.keys(form).forEach((key) => {
      if (form[key] !== null && form[key] !== undefined) {
        formData.append(key, form[key]);
      }
    });

    try {
      const endpoint = this.isEditMode ? `/products/${id}?_method=PUT` : "/products";
      const res = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.status === "success") {
        Swal.fire("Success", res.data.message, "success");
        this.props.router.navigate("/admin/products");
      } else {
        Swal.fire("Error", res.data.message || "Failed to save product", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to save product", "error");
    }
  };

  render() {
    const { form, options } = this.state;

    return (
      <div className="pc-container">
        <div className="pc-content">
          <div className="row">
            <div className="col-12">
              <div className="card shadow-sm border-0 rounded-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <ul className="breadcrumb mb-0">
                    <li className="breadcrumb-item">Product</li>
                    <li className="breadcrumb-item">{this.isEditMode ? "Edit" : "Add"}</li>
                  </ul>
                  <Link to="/admin/products" className="btn btn-light-warning">
                    Product List
                  </Link>
                </div>

                <div className="card-body p-4">
                  <form onSubmit={this.handleSubmit}>
                    {/* GENERAL INFO */}
                    <h6 className="fw-semibold text-uppercase text-muted mb-3">
                      General Information
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-8">
                        <label className="form-label">Product Name *</label>
                        <input
                          type="text"
                          name="product_name"
                          value={form.product_name}
                          onChange={this.handleChange}
                          className="form-control"
                          placeholder="Enter product name"
                          required
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Brand</label>
                        <select
                          name="brand_id"
                          value={form.brand_id}
                          onChange={this.handleChange}
                          className="form-select"
                        >
                          <option value="">Select brand</option>
                          {options.brands.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.brand_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-8">
                        <label className="form-label">Description</label>
                        <textarea
                          name="description"
                          value={form.description}
                          onChange={this.handleChange}
                          className="form-control"
                          rows={3}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Product Image</label>
                        <input
                          type="file"
                          name="product_img"
                          onChange={this.handleChange}
                          className="form-control"
                        />
                        {this.isEditMode && (
                          <small className="text-muted">
                            Current image will be kept if not changed
                          </small>
                        )}
                      </div>
                    </div>

                    {/* CATEGORY & TAGS */}
                    <hr className="my-4" />
                    <h6 className="fw-semibold text-uppercase text-muted mb-3">
                      Categories & Tags
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">Category</label>
                        <select
                          name="category_id"
                          value={form.category_id}
                          onChange={this.handleChange}
                          className="form-select"
                        >
                          <option value="">Select category</option>
                          {options.categories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.category_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Sub Category</label>
                        <select
                          name="sub_category_id"
                          value={form.sub_category_id}
                          onChange={this.handleChange}
                          className="form-select"
                        >
                          <option value="">Select sub category</option>
                          {options.subCategories.map((sc) => (
                            <option key={sc.id} value={sc.id}>
                              {sc.sub_category_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Child Category</label>
                        <select
                          name="child_category_id"
                          value={form.child_category_id}
                          onChange={this.handleChange}
                          className="form-select"
                        >
                          <option value="">Select child category</option>
                          {options.childCategories.map((cc) => (
                            <option key={cc.id} value={cc.id}>
                              {cc.child_category_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Tags</label>
                        <input
                          type="text"
                          ref={this.tagInputRef}
                          className="form-control"
                          placeholder="Type & select tags"
                        />
                      </div>
                    </div>

                    {/* PRICING */}
                    <hr className="my-4" />
                    <h6 className="fw-semibold text-uppercase text-muted mb-3">Pricing & Stock</h6>
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="form-label">Regular Price</label>
                        <input
                          type="number"
                          name="regular_price"
                          value={form.regular_price}
                          onChange={this.handleChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Sales Price</label>
                        <input
                          type="number"
                          name="sales_price"
                          value={form.sales_price}
                          onChange={this.handleChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Cost Price</label>
                        <input
                          type="number"
                          name="cost_price"
                          value={form.cost_price}
                          onChange={this.handleChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Stock Value</label>
                        <input
                          type="number"
                          name="stock_value"
                          value={form.stock_value}
                          onChange={this.handleChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">SKU</label>
                        <input
                          type="text"
                          name="sku"
                          value={form.sku}
                          onChange={this.handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    {/* SPECIFICATIONS */}
                    <hr className="my-4" />
                    <h6 className="fw-semibold text-uppercase text-muted mb-3">
                      Product Specifications
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">Product Specification</label>
                        <input
                          type="text"
                          name="product_specification"
                          value={form.product_specification}
                          onChange={this.handleChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Model</label>
                        <input
                          type="text"
                          name="model"
                          value={form.model}
                          onChange={this.handleChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Other Specifications</label>
                        <input
                          type="text"
                          name="others_specification"
                          value={form.others_specification}
                          onChange={this.handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    {/* SEO SETTINGS */}
                    <hr className="my-4" />
                    <h6 className="fw-semibold text-uppercase text-muted mb-3">SEO Settings</h6>
                    <div className="row g-3">
                      <div className="col-md-8">
                        <label className="form-label">SEO Title</label>
                        <input
                          type="text"
                          name="seo_title"
                          value={form.seo_title}
                          onChange={this.handleChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-8">
                        <label className="form-label">SEO Description</label>
                        <textarea
                          name="seo_description"
                          value={form.seo_description}
                          onChange={this.handleChange}
                          className="form-control"
                          rows={2}
                        />
                      </div>
                      <div className="col-md-8">
                        <label className="form-label">SEO Keywords</label>
                        <textarea
                          name="seo_keywords"
                          value={form.seo_keywords}
                          onChange={this.handleChange}
                          className="form-control"
                          rows={2}
                        />
                      </div>
                    </div>

                    <div className="text-end mt-4">
                      <button type="reset" className="btn btn-light border me-2">
                        Clear
                      </button>
                      <button type="submit" className="btn btn-primary px-4">
                        {this.isEditMode ? "Update Product" : "Submit Product"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(ProductForm);
